import classNames from 'classnames';
import React, { useCallback, useState } from 'react';
import {
	EventType,
	IBaseListAction,
	IChatEventFormMessage,
	IChatMessagesOut,
	IAccordionItem,
	IMessageCommandMeta,
	MessageType,
} from '@enuan/chat__utils';
import { styled } from '@mui/material/styles';
import { Collapsible } from '../../../UI/Collapsible/Collapsible';
import { Form } from '../../../form/Form';
import { useParseText } from '../../../../hooks/useParseText';
import { useSendMessage } from '../../../../hooks';
import { getFormLabels } from '../../../../utils/getFormLabels';
import { useLogError } from '../../../ErrorBoundary';
import type { ICollapsibleProps } from '../../../UI/Collapsible/Collapsible';

export type IAccordionItemUIProps = Omit<
	ICollapsibleProps,
	'toggle' | 'softHide' | 'isOpen'
> & { itemKey: React.Key };

type IAccordionItemProps = Omit<IAccordionItemUIProps, 'children'> & {
	softHide?: boolean;
	openItems: React.Key[];
	toggleOpen: (key: React.Key, ref: HTMLElement) => void;
	card: IAccordionItem;
	prefix: string;
	command_id: IMessageCommandMeta['id'];
	onAction?: (action: IBaseListAction) => void;
};
export const AccordionItem = styled(
	({
		card,
		itemKey,
		className,
		openItems,
		toggleOpen,
		softHide,
		prefix,
		command_id: metadata_id,
		onAction,
		...props
	}: IAccordionItemProps) => {
		const { image, title, description, text, actions, form } = card;

		const logError = useLogError('AccordionContent', {
			form,
			command_id: metadata_id,
		});

		const { html: description_html, text: description_text } =
			useParseText(text);

		const [readOnly, setReadOnly] = useState(false);
		const sendMessage = useSendMessage();

		const onSubmitAction = useCallback(
			(values: Record<string, any>) => {
				const formLabels = getFormLabels(form?.items, values);

				const event: IChatMessagesOut<IChatEventFormMessage> = {
					_type: MessageType.EVENT,
					_data: {
						name: EventType.POSTBACK,
						parameters: {
							payload: values,
							metadata: {
								command_id: metadata_id,
								labels: {
									form: formLabels,
									header: {
										...form?.header,
										title: form?.header?.title ?? title,
									},
								},
							},
						},
					},
				};

				setReadOnly(true);
				sendMessage(event).catch((error) => {
					logError('submit', { error, payload: { event } });
					setReadOnly(false);
				});
				return true;
			},
			[title, form?.items, metadata_id, sendMessage, form?.header, logError],
		);

		return (
			<Collapsible
				className={classNames('accordion__item', className)}
				{...props}
				isOpen={openItems.includes(itemKey)}
				softHide={softHide}
				toggle={(e) => {
					toggleOpen(itemKey, e);
				}}
				headerProps={{ className: 'accordion__header' }}
				header={
					<>
						{image && <img src={image} alt="" />}
						<main className="text">
							<span className="title">{title}</span>
							{description && (
								<small className="description">{description}</small>
							)}
						</main>
					</>
				}
				dividerProps={{ className: 'accordion__divider' }}
				mainProps={{ className: 'accordion__content' }}
			>
				<main className="content__main">
					<div
						className="content__description"
						dangerouslySetInnerHTML={{ __html: description_html ?? '' }}
						title={description_text}
					/>

					{form && form.items && (
						<AccordionForm
							className="message__form form__small"
							prefix={prefix}
							onSubmitAction={onSubmitAction}
							readOnly={readOnly}
							formConfig={form}
						/>
					)}
				</main>

				<footer className="content__footer">
					{(Array.isArray(actions) ? actions : [actions]).map(
						(action, index) => (
							<button
								className="action"
								key={index}
								onClick={(e) => {
									e.preventDefault();
									onAction?.(action);
								}}
							>
								{action.label}
							</button>
						),
					)}
				</footer>
			</Collapsible>
		);
	},
	{ label: 'AccordionItem', target: 'AccordionItem-root' },
)({
	background: 'var(--message__accordion_background)',
	borderRadius: '0 6px 6px 6px',
	'&:not(:first-child)': {
		marginTop: '4px',
	},
	'& .accordion__header': {
		borderRadius: 'inherit',
		paddingTop: 0,
		paddingLeft: 0,
		paddingBottom: 0,
		background: 'var(--message__accordion__header_background)',
		'& img': {
			width: 38,
			height: 38,
			borderRadius: '0 6px 6px 6px',
			objectFit: 'none',
			objectPosition: 'center',
			flexShrink: 0,
		},
		'& main.text': {
			padding: '0 8px 0 8px',
			overflow: 'hidden',
			display: 'flex',
			flexDirection: 'column',
			justifyContent: 'center',
			flex: 1,
			minWidth: 0,
			'& .title, & .description': {
				whiteSpace: 'nowrap',
				overflow: 'hidden',
				textOverflow: 'ellipsis',
				display: 'block',
			},
			'& .title': {
				color: 'var(--message__accordion__title_color)',
				textTransform: 'capitalize',
			},
			'& .description': {
				color: 'var(--message__accordion__description_color)',
				fontSize: 'x-small',
				textTransform: 'capitalize',
			},
		},
	},
	'&.collapsible--open .accordion__header': {
		borderRadius: '0 6px 0 0',
	},
	'& .accordion__content': {
		borderRadius: '0 0 6px 6px',
	},
});

const AccordionForm = styled(Form)({
	'&.form__small .form__row': {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'flex-start',
		justifyContent: 'space-between',
		marginBottom: '0.2em',
		marginTop: '0.2em',
		'& .form__row__input': {
			width: '50%',
			marginRight: 0,
		},
		'& .form__label': {
			display: 'inline-block',
			fontSize: '1.1em',
			fontWeight: 'normal',
			marginTop: '0.6em',
		},
		'& .textarea, & .input, & .select__control, & .select__menu': {
			borderWidth: '1px',
			borderStyle: 'solid',
			borderColor: 'var(--message__accordion__input_border) !important',
		},
		'& .select__menu': {
			marginTop: '-2px',
		},
		'& .select .select__control': {
			'&.select__control--is-focused': {
				borderRadius: '0.4em 0.4em 0 0',
			},
		},
		'& .textarea, & .input, & .select__control, & .select__option': {
			fontSize: '1.1em !important',
		},
		'& .input-date + .arrow__icon, & .select__indicator': {
			padding: 0,
		},
		'& .input-date + .arrow__icon': {
			height: '1.8em',
			width: '1.8em',
			'& svg': {
				height: '1.7em',
				width: '1.7em',
				opacity: 1,
			},
		},
		'& .select__indicator svg': {
			height: '1.5em',
			width: '1.5em',
		},
		'& .select__menu-list': {
			marginTop: '-0.6em',
		},
		'& .form__error': {
			display: 'none',
		},
		'&.form__submit .form__row__input': {
			width: '100%',
			'& .button': {
				marginTop: '0.8em',
				cursor: 'pointer',
				fontSize: '1.3em',
				padding: '0 0 0 0.8em',
				background: 'transparent',
				textTransform: 'capitalize',
				fontFamily: 'Roboto, sans-serif',
				fontWeight: 'bold',
				color: 'var(--message__accordion__form__submit_color)',
				'&::after': {
					content: '" >"',
				},
			},
		},
	},
});
